import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import FetchingSpinner from "../../common/FetchingSpinner";
import { Common, WebStorage, WebStorageNames } from "../../../utils";
import {
  addTicketComment,
  getTicket,
  updateTicket,
} from "./ticketApi";
import {
  formatTicketDate,
  PRIORITY_OPTIONS,
  PriorityBadge,
  STATUS_OPTIONS,
  StatusBadge,
} from "./helpSupportShared";
import "../documents/Documents.css";
import "./HelpSupport.css";

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  return Number(r);
};

const HelpSupportDetailPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const isAdmin = getRoleId() === 1 || getRoleId() === 2;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [adminForm, setAdminForm] = useState({
    status: "",
    priority: "",
  });

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const detail = await getTicket(id);
      setTicket(detail);
      setAdminForm({
        status: String(detail?.status ?? detail?.Status ?? ""),
        priority: String(detail?.priority ?? detail?.Priority ?? ""),
      });
    } catch (err) {
      setError(err?.message || t("app.helpSupport.loadFailed", { defaultValue: "Failed to load ticket." }));
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      Common.showErrorMessage(
        t("app.helpSupport.commentRequired", { defaultValue: "Please enter a comment." })
      );
      return;
    }

    setSaving(true);
    try {
      await addTicketComment({
        ticketId: id,
        comment: comment.trim(),
        isInternal: isAdmin ? isInternal : false,
      });
      setComment("");
      setIsInternal(false);
      Common.showSuccessMessage(
        t("app.helpSupport.commentAdded", { defaultValue: "Comment added." })
      );
      await load();
    } catch (err) {
      Common.showErrorMessage(err?.message || t("app.modals.helpSupport.errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const handleAdminUpdate = async () => {
    setSaving(true);
    try {
      const updated = await updateTicket({
        ticketId: id,
        status: adminForm.status === "" ? null : Number(adminForm.status),
        priority: adminForm.priority === "" ? null : Number(adminForm.priority),
      });
      setTicket(updated);
      Common.showSuccessMessage(
        t("app.helpSupport.updatedSuccess", { defaultValue: "Ticket updated." })
      );
    } catch (err) {
      Common.showErrorMessage(err?.message || t("app.modals.helpSupport.errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="help-support-page documents-page">
        <FetchingSpinner />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="help-support-page documents-page">
        <CommonPageHeader
          icon={<i className="bi bi-life-preserver" />}
          title={t("app.header.helpAndSupport")}
          onBack={() => history.push("/help-support")}
        />
        <div className="help-support-error">
          {error || t("app.helpSupport.notFound", { defaultValue: "Ticket not found." })}
        </div>
      </div>
    );
  }

  const title = ticket.title ?? ticket.Title ?? "—";
  const description = ticket.description ?? ticket.Description ?? "";
  const problemType = ticket.problemType ?? ticket.ProblemType ?? "—";
  const status = ticket.status ?? ticket.Status;
  const statusName = ticket.statusName ?? ticket.StatusName;
  const priority = ticket.priority ?? ticket.Priority;
  const priorityName = ticket.priorityName ?? ticket.PriorityName;
  const createdByName = ticket.createdByName ?? ticket.CreatedByName ?? "—";
  const assignedToName = ticket.assignedToName ?? ticket.AssignedToName;
  const createdDate = ticket.createdDate ?? ticket.CreatedDate;
  const updatedDate = ticket.updatedDate ?? ticket.UpdatedDate;
  const comments = ticket.comments ?? ticket.Comments ?? [];

  return (
    <div className="help-support-page documents-page">
      <CommonPageHeader
        icon={<i className="bi bi-ticket-detailed" />}
        title={t("app.helpSupport.ticketDetail", { defaultValue: "Ticket detail" })}
        description={title}
        onBack={() => history.push("/help-support")}
        backLabel={t("app.common.back", { defaultValue: "Back" })}
      />

      {error ? <div className="help-support-error">{error}</div> : null}

      <div className="help-support-detail-card">
        <div className="help-support-badges" style={{ marginBottom: "0.75rem" }}>
          <StatusBadge status={status} statusName={statusName} />
          <PriorityBadge priority={priority} priorityName={priorityName} />
        </div>
        <h2 className="help-support-detail-title">{title}</h2>
        <div className="help-support-ticket-meta">
          <span>{problemType}</span>
          <span>
            {t("app.helpSupport.createdBy", { defaultValue: "Created by" })}: {createdByName}
          </span>
          {assignedToName ? (
            <span>
              {t("app.helpSupport.assignedTo", { defaultValue: "Assigned to" })}: {assignedToName}
            </span>
          ) : null}
          <span>
            {t("app.helpSupport.created", { defaultValue: "Created" })}: {formatTicketDate(createdDate)}
          </span>
          <span>
            {t("app.helpSupport.updated", { defaultValue: "Updated" })}: {formatTicketDate(updatedDate)}
          </span>
        </div>
        <p className="help-support-detail-description">{description}</p>

        {isAdmin ? (
          <div className="help-support-admin-panel">
            <div className="help-support-field">
              <label htmlFor="admin-status">
                {t("app.helpSupport.status", { defaultValue: "Status" })}
              </label>
              <select
                id="admin-status"
                value={adminForm.status}
                onChange={(e) => setAdminForm((prev) => ({ ...prev, status: e.target.value }))}
                disabled={saving}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="help-support-field">
              <label htmlFor="admin-priority">
                {t("app.helpSupport.priority", { defaultValue: "Priority" })}
              </label>
              <select
                id="admin-priority"
                value={adminForm.priority}
                onChange={(e) => setAdminForm((prev) => ({ ...prev, priority: e.target.value }))}
                disabled={saving}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="help-support-field" style={{ justifyContent: "flex-end" }}>
              <label>&nbsp;</label>
              <button
                type="button"
                className="help-support-primary-btn"
                onClick={handleAdminUpdate}
                disabled={saving}
              >
                {t("app.helpSupport.saveChanges", { defaultValue: "Save changes" })}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="help-support-detail-card">
        <h3 className="help-support-section-title">
          {t("app.helpSupport.activity", { defaultValue: "Activity" })}
        </h3>

        {comments.length === 0 ? (
          <div className="help-support-empty">
            {t("app.helpSupport.noComments", {
              defaultValue: "No updates yet. Add a comment to continue the conversation.",
            })}
          </div>
        ) : (
          <div className="help-support-comments">
            {comments.map((c) => {
              const commentId = c.id ?? c.Id;
              const body = c.comment ?? c.Comment ?? "";
              const author = c.commentedByName ?? c.CommentedByName ?? "—";
              const date = c.commentedDate ?? c.CommentedDate;
              const internal = Boolean(c.isInternal ?? c.IsInternal);
              return (
                <div
                  key={commentId}
                  className={`help-support-comment ${internal ? "is-internal" : ""}`}
                >
                  <div className="help-support-comment-head">
                    <span className="help-support-comment-author">{author}</span>
                    <span>{formatTicketDate(date)}</span>
                    {internal ? (
                      <span className="help-support-badge help-support-badge--internal">
                        {t("app.helpSupport.internal", { defaultValue: "Internal" })}
                      </span>
                    ) : null}
                  </div>
                  <p className="help-support-comment-body">{body}</p>
                </div>
              );
            })}
          </div>
        )}

        <form className="help-support-comment-box" onSubmit={handleAddComment}>
          <label htmlFor="ticket-comment" className="help-support-section-title">
            {t("app.helpSupport.addUpdate", { defaultValue: "Add an update" })}
          </label>
          <textarea
            id="ticket-comment"
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("app.helpSupport.commentPlaceholder", {
              defaultValue: "Share more detail or ask a follow-up question…",
            })}
            disabled={saving}
            maxLength={2000}
          />
          <div className="help-support-comment-actions">
            {isAdmin ? (
              <label className="help-support-checkbox">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  disabled={saving}
                />
                {t("app.helpSupport.internalNote", {
                  defaultValue: "Internal note (hidden from requester)",
                })}
              </label>
            ) : (
              <span />
            )}
            <button type="submit" className="help-support-primary-btn" disabled={saving}>
              {t("app.helpSupport.postComment", { defaultValue: "Post comment" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HelpSupportDetailPage;
